import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
  Link,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useToast } from '../contexts/ToastContext';
import MapIcon from '@mui/icons-material/Map';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GeofenceMapEditor, { type GeofenceGeoJSON } from '../components/GeofenceMapEditor';
import GateMapEditor, { type GateMapEditorResult, type RequestAreaShape, circleToGeoJSONRing } from '../components/GateMapEditor';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import TvDisplayOtpSection from '../components/settings/TvDisplayOtpSection';
import { openStaticQRPrintPage } from '../utils/staticQrPrint';
import { STAFF_ROLE_TYPES, SETTINGS_STAFF_FEATURES as STAFF_FEATURES, STAFF_ROLES } from '../constants';
import { GateItem, SchoolsResponse, UpdateSchoolPayload } from '../types';

const SETTINGS_TABS = [
  { value: 'dispersal', path: 'dispersal', label: 'Dispersal' },
] as const;

const Settings: React.FC<{ standaloneTab?: 'geofence' | 'gates' }> = ({ standaloneTab }) => {
  const queryClient = useQueryClient();
  const user = authService.getStoredUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const { data: schoolsData, refetch: refetchSchools } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];

  const [access, setAccess] = useState<Record<string, string>>({});
  const [featureAccess, setFeatureAccess] = useState<Record<string, Record<string, { view: boolean; edit: boolean }>>>({});
  const [qrExpiry, setQrExpiry] = useState(30);
  const [gateMapDialogGateIdx, setGateMapDialogGateIdx] = useState<number | null>(null);
  const [geofenceRequiredForQRScan, setGeofenceRequiredForQRScan] = useState(true);
  const [geofenceRequiredForRequestButton, setGeofenceRequiredForRequestButton] = useState(true);
  const [useTvDisplay, setUseTvDisplay] = useState(true);
  const [gates, setGates] = useState<GateItem[]>([]);
  const [autoConfirmEnabled, setAutoConfirmEnabled] = useState(true);
  const [autoConfirmPickupTime, setAutoConfirmPickupTime] = useState('18:00');
  const [pickupAutoConfirmAfterMinutes, setPickupAutoConfirmAfterMinutes] = useState('');
  const [requestAgainWaitMinutes, setRequestAgainWaitMinutes] = useState(2);
  const [sendFromClassRestriction, setSendFromClassRestriction] = useState('ANY_TEACHER');
  const [earlyDispersalTeacherCanApprove, setEarlyDispersalTeacherCanApprove] = useState(true);
  const [earlyDispersalPrincipalCanApprove, setEarlyDispersalPrincipalCanApprove] = useState(true);

  const settingsTab = (location.pathname.match(/^\/settings\/([^/]+)/) || [null, 'dispersal'])[1];
  const currentTab = standaloneTab ?? (SETTINGS_TABS.some((t) => t.path === settingsTab) ? settingsTab : 'dispersal');
  const showTabs = !standaloneTab;
  const { canEdit: canEditSettings } = useFeatureAccess('settings');
  const { canEdit: canEditGates } = useFeatureAccess('gates');
  const { canEdit: canEditSchool } = useFeatureAccess('school');

  React.useEffect(() => {
    const p = location.pathname.replace(/\/$/, '');
    if (p === '/settings') navigate('/settings/dispersal', { replace: true });
    if (p === '/settings/dispersal') navigate('/settings/dispersal', { replace: true });
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    if (currentTab === 'geofence' || currentTab === 'gates') refetchSchools();
  }, [currentTab, refetchSchools]);

  React.useEffect(() => {
    if (school) {
      const next: Record<string, string> = {};
      const staffAccessFromSchool = school.settings?.staffStudentAccess || {};
      [...STAFF_ROLES, ...STAFF_ROLE_TYPES.map((r) => r.value)].filter((v, i, a) => a.indexOf(v) === i).forEach((r) => {
        const v = staffAccessFromSchool[r];
        next[r] = v || (r === 'TEACHER' ? 'CLASS_ONLY' : 'NONE');
      });
      setAccess(next);
      const rawFeature = school.settings?.staffFeatureAccess || {};
      const nextFeature: Record<string, Record<string, { view: boolean; edit: boolean }>> = {};
      const defaultViewForTeacher = ['dashboard', 'dispersals', 'students', 'notifications', 'meetings'];
      const defaultEditForTeacher = ['dispersals'];
      STAFF_ROLE_TYPES.forEach(({ value: roleType }) => {
        nextFeature[roleType] = {};
        STAFF_FEATURES.forEach((f) => {
          const current = rawFeature[roleType]?.[f.id];
          const defaultView = roleType === 'TEACHER' && defaultViewForTeacher.includes(f.id);
          const defaultEdit = roleType === 'TEACHER' && defaultEditForTeacher.includes(f.id);
          nextFeature[roleType][f.id] = {
            view: current?.view !== undefined ? !!current.view : defaultView,
            edit: current?.edit !== undefined ? !!current.edit : defaultEdit,
          };
        });
      });
      setFeatureAccess(nextFeature);
      setQrExpiry(school.settings?.qrExpirySeconds ?? 30);
      setGeofenceRequiredForQRScan(school.settings?.geofenceRequiredForQRScan !== false);
      setGeofenceRequiredForRequestButton(school.settings?.geofenceRequiredForRequestButton !== false);
      setUseTvDisplay(school.settings?.useTvDisplay !== false);
      setAutoConfirmEnabled(school.settings?.autoConfirmEnabled !== false);
      setAutoConfirmPickupTime(school.settings?.autoConfirmPickupTime || '18:00');
      setPickupAutoConfirmAfterMinutes(school.settings?.pickupAutoConfirmAfterMinutes != null ? String(school.settings.pickupAutoConfirmAfterMinutes) : '');
      setRequestAgainWaitMinutes(Math.max(0, Number(school.settings?.requestAgainWaitMinutes) || 2));
      setSendFromClassRestriction(school.settings?.sendFromClassRestriction || 'ANY_TEACHER');
      setEarlyDispersalTeacherCanApprove(school.settings?.earlyDispersalTeacherCanApprove ?? school.settings?.earlyDismissalTeacherCanApprove !== false);
      setEarlyDispersalPrincipalCanApprove(school.settings?.earlyDispersalPrincipalCanApprove ?? school.settings?.earlyDismissalPrincipalCanApprove !== false);
      const rawGates = school.settings?.dispersalGates?.length ? JSON.parse(JSON.stringify(school.settings.dispersalGates)) : [];
      const legacyEarlyGateId = school.settings?.earlyDispersalGateId ?? school.settings?.earlyDismissalGateId;
      if (legacyEarlyGateId && rawGates.length) {
        rawGates.forEach((g: GateItem) => {
          if (g.gateId === legacyEarlyGateId) g.earlyDispersalEnabled = true;
        });
      }
      setGates(rawGates);
    }
  }, [school]);

  const updateMutation = useMutation(
    async (payload: UpdateSchoolPayload) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Settings saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const saveGeofenceMutation = useMutation(
    async (geofence: GeofenceGeoJSON | null) => {
      if (!school) throw new Error('No school');
      const payload = geofence == null
        ? { geofence: null }
        : { geofence: JSON.parse(JSON.stringify(geofence)) };
      const res = await apiClient.put(`/schools/${school._id}`, payload);
      return res.data;
    },
    {
      onSuccess: (_, geofence) => {
        queryClient.invalidateQueries('schools');
        showToast('success', geofence == null ? 'School geofence cleared.' : 'School geofence saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save geofence');
      },
    }
  );

  const handleSave = () => {
    if (!school || updateMutation.isLoading) return;
    const isAuthorityMode = school.settings?.staffFeaturePermissionsMode === 'AUTHORITY';
    const staffStudentAccess: Record<string, string> = {};
    STAFF_ROLE_TYPES.forEach(({ value: r }) => { staffStudentAccess[r] = access[r] ?? (r === 'TEACHER' ? 'CLASS_ONLY' : 'NONE'); });
    STAFF_ROLES.forEach((r) => { if (staffStudentAccess[r] === undefined) staffStudentAccess[r] = access[r] || 'NONE'; });
    const payload: UpdateSchoolPayload = {
      ...school,
      _id: school._id,
      settings: {
        ...school.settings,
        ...(isAuthorityMode
          ? {}
          : {
              staffStudentAccess,
              staffFeatureAccess: featureAccess,
            }),
        qrExpirySeconds: Math.min(300, Math.max(10, qrExpiry)),
        geofenceRequiredForQRScan: geofenceRequiredForQRScan,
        geofenceRequiredForRequestButton: geofenceRequiredForRequestButton,
        useTvDisplay,
        currentDispersalClass: '',
        currentDispersalSection: '',
        dispersalGates: gates.map((g) => ({
          ...g,
          enabled: g.enabled !== false,
          earlyDispersalEnabled: !!(g.earlyDispersalEnabled ?? g.earlyDismissalEnabled),
          allowedClasses: (g.allowedClasses || []).filter(
            (ac) => String(ac?.class ?? '').trim() !== ''
          ).map((ac) => ({
            class: ac.class,
            section: ac.section ?? '',
            enabled: ac.enabled !== false,
            startTime: ac.startTime ?? '',
            endTime: ac.endTime ?? '',
            requireQRScanAtGate: ac.requireQRScanAtGate !== false,
            allowRequestWithoutQR: !!ac.allowRequestWithoutQR,
          })),
        })),
        autoConfirmEnabled,
        autoConfirmPickupTime: autoConfirmPickupTime || '18:00',
        pickupAutoConfirmAfterMinutes: pickupAutoConfirmAfterMinutes === '' ? null : (Number(pickupAutoConfirmAfterMinutes) || null),
        requestAgainWaitMinutes: Math.max(0, Number(requestAgainWaitMinutes) || 2),
        sendFromClassRestriction,
        earlyDispersalTeacherCanApprove,
        earlyDispersalPrincipalCanApprove,
      },
    };
    updateMutation.mutate(payload);
  };

  const classSectionsList = school?.settings?.classSections ?? [];

  const addGate = () => {
    const id = `gate-${Date.now()}`;
    setGates((g) => [...g, { gateId: id, name: `Gate ${g.length + 1}`, enabled: true, allowedClasses: [{ class: '', section: '', enabled: true, startTime: '', endTime: '', requireQRScanAtGate: true, allowRequestWithoutQR: false }] }]);
  };

  const removeGate = (idx: number) => {
    setGates((g) => g.filter((_, i) => i !== idx));
  };

  const updateGate = (idx: number, field: string, value: string | number | boolean | GeofenceGeoJSON | RequestAreaShape | null) => {
    setGates((g) => {
      const next = [...g];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const updateGateMapResult = (idx: number, result: GateMapEditorResult) => {
    setGates((g) => {
      const next = [...g];
      next[idx] = {
        ...next[idx],
        latitude: result.latitude,
        longitude: result.longitude,
        requestArea: result.requestArea ?? undefined,
      };
      return next;
    });
    setGateMapDialogGateIdx(null);
  };

  const addAllowedClass = (gateIdx: number) => {
    setGates((g) => {
      const next = [...g];
      next[gateIdx].allowedClasses = [...(next[gateIdx].allowedClasses || []), { class: '', section: '', enabled: true, startTime: '', endTime: '', requireQRScanAtGate: true, allowRequestWithoutQR: false }];
      return next;
    });
  };

  const updateAllowedClass = (gateIdx: number, classIdx: number, field: string, value: string | boolean) => {
    setGates((g) => {
      const next = [...g];
      const ac = [...(next[gateIdx].allowedClasses || [])];
      ac[classIdx] = { ...ac[classIdx], [field]: value };
      next[gateIdx].allowedClasses = ac;
      return next;
    });
  };

  const removeAllowedClass = (gateIdx: number, classIdx: number) => {
    setGates((g) => {
      const next = [...g];
      next[gateIdx].allowedClasses = (next[gateIdx].allowedClasses || []).filter((_, i) => i !== classIdx);
      return next;
    });
  };

  if (!user || !school) return <Box><Typography>Loading...</Typography></Box>;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 1 }}>
        {standaloneTab === 'geofence' ? 'School' : standaloneTab === 'gates' ? 'Gates' : 'School Settings'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {standaloneTab === 'geofence'
          ? 'Geofence ensures dispersal requests are only allowed when the parent is within school premises.'
          : standaloneTab === 'gates'
            ? 'Configure each dispersal gate (TV or printed QR). Parents can request only at the gate that allows their class/section.'
            : 'Principal and Admin can configure dispersal settings. Use Staff Permissions for access control; School and Gates for geofence and gates.'}
      </Typography>

      {showTabs && (
        <Tabs
          value={currentTab}
          onChange={(_, v) => navigate(`/settings/${v}`)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {SETTINGS_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.path} label={tab.label} />
          ))}
        </Tabs>
      )}

      {currentTab === 'dispersal' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>TV / Dispersal</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure QR expiry, geofence requirement, TV display, staff permissions, and pickup confirmation. Configure gates in the Gates tab.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField type="number" label="QR expiry (seconds)" value={qrExpiry} onChange={(e) => setQrExpiry(Number(e.target.value) || 30)} inputProps={{ min: 10, max: 300 }} sx={{ width: 140 }} />
              <FormControlLabel control={<Switch checked={geofenceRequiredForQRScan} onChange={(e) => setGeofenceRequiredForQRScan(e.target.checked)} />} label="Require parent inside school geofence for QR scan" />
              <FormControlLabel control={<Switch checked={geofenceRequiredForRequestButton} onChange={(e) => setGeofenceRequiredForRequestButton(e.target.checked)} />} label="Require parent inside school geofence for Request button" />
              <FormControlLabel control={<Switch checked={useTvDisplay} onChange={(e) => setUseTvDisplay(e.target.checked)} />} label="Use TV display (else use printed QR)" />
            </Box>

            {useTvDisplay && (
              <TvDisplayOtpSection schoolId={school._id} />
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>Staff dispersal permissions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Who can send a student from class after parent request, and who can approve early dispersal requests.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Who can send student from class"
              value={sendFromClassRestriction}
              onChange={(e) => setSendFromClassRestriction(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="ANY_TEACHER">Any teacher (and Principal/Admin)</MenuItem>
              <MenuItem value="ASSIGNED_CLASS_ONLY">Only teacher assigned to student&apos;s class/section (and Principal/Admin)</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={<Switch checked={earlyDispersalTeacherCanApprove} onChange={(e) => setEarlyDispersalTeacherCanApprove(e.target.checked)} />}
                label="Teachers can approve early dispersal requests (first level)"
              />
              <FormControlLabel
                control={<Switch checked={earlyDispersalPrincipalCanApprove} onChange={(e) => setEarlyDispersalPrincipalCanApprove(e.target.checked)} />}
                label="Principal/Admin can approve early dispersal requests (second level)"
              />
              <Typography variant="body2" color="text.secondary">
                To allow early dispersal pickup at a gate, enable &quot;Enable this gate for Early Dispersal&quot; on the Gates tab for that gate.
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>Pickup confirmation (parent app)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              After staff sends a student from class, parents can confirm pickup. If they don&apos;t confirm, pickup can be auto-confirmed by time of day and/or after a delay.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={autoConfirmEnabled} onChange={(e) => setAutoConfirmEnabled(e.target.checked)} />}
                label="Auto-confirm pickup enabled"
              />
              <TextField
                size="small"
                label="Daily auto-confirm time"
                value={autoConfirmPickupTime}
                onChange={(e) => setAutoConfirmPickupTime(e.target.value)}
                placeholder="18:00"
                sx={{ width: 140 }}
                helperText="e.g. 18:00"
              />
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 0, step: 15 }}
                label="Or auto-confirm after (minutes)"
                value={pickupAutoConfirmAfterMinutes}
                onChange={(e) => setPickupAutoConfirmAfterMinutes(e.target.value)}
                placeholder="e.g. 120 = 2 hours"
                sx={{ width: 200 }}
                helperText="Leave empty to use only daily time"
              />
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 0, max: 60, step: 1 }}
                label="Request again wait (minutes)"
                value={requestAgainWaitMinutes}
                onChange={(e) => setRequestAgainWaitMinutes(Math.max(0, Number(e.target.value) || 0))}
                sx={{ width: 160 }}
                helperText="At gate: minutes before parent can tap Request again"
              />
            </Box>
          </Box>
          {canEditSettings && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={updateMutation.isLoading} sx={{ mt: 2 }}>Save dispersal settings</Button>
          )}
        </Paper>
      )}

      {currentTab === 'gates' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Gates</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure each dispersal gate (TV or printed QR). Parents can request only at the gate that allows their class/section. Set enable/disable, request area, and allowed classes per gate.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {gates.map((gate, idx) => (
              <Paper key={gate.gateId} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <TextField size="small" label="Gate name" value={gate.name} onChange={(e) => updateGate(idx, 'name', e.target.value)} placeholder="e.g. Main Gate" sx={{ width: 200 }} disabled={!canEditGates} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {canEditGates ? (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!(gate.earlyDispersalEnabled ?? gate.earlyDismissalEnabled)}
                              onChange={(e) => updateGate(idx, 'earlyDispersalEnabled', e.target.checked)}
                            />
                          }
                          label="Enable this gate for Early Dispersal"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={gate.enabled !== false}
                              onChange={(e) => updateGate(idx, 'enabled', e.target.checked)}
                            />
                          }
                          label={gate.enabled !== false ? 'Gate enabled' : 'Gate disabled'}
                        />
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {gate.enabled !== false ? 'Enabled' : 'Disabled'}
                        {(gate.earlyDispersalEnabled ?? gate.earlyDismissalEnabled) && ' · Early Dispersal'}
                      </Typography>
                    )}
                    {canEditGates && <IconButton size="small" onClick={() => removeGate(idx)}><DeleteIcon /></IconButton>}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {canEditGates && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<MapIcon />}
                    onClick={() => setGateMapDialogGateIdx(idx)}
                  >
                    Set pickup point &amp; request area
                  </Button>
                  )}
                  {gate.latitude != null && gate.longitude != null && (
                    <Typography variant="caption" color="text.secondary">
                      Pickup pin set • {(gate.requestArea && (Array.isArray((gate.requestArea as { coordinates?: unknown[] }).coordinates?.[0]) || (gate.requestArea as { type?: string }).type === 'Circle')) ? 'Request area set' : 'No request area'}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">Allowed class/section at this gate — enable/disable and set dispersal time window per class. You can allow QR scan only, Request for dispersal only, or both.</Typography>
                {(gate.allowedClasses || []).map((ac, cIdx) => (
                  <Box key={cIdx} sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={ac.enabled !== false}
                            onChange={() => updateAllowedClass(idx, cIdx, 'enabled', ac.enabled === false)}
                            disabled={!canEditGates}
                          />
                        }
                        label={ac.enabled !== false ? 'Enabled' : 'Disabled'}
                      />
                      {classSectionsList.length > 0 ? (
                        <TextField
                          select
                          size="small"
                          label="Class / Section"
                          value={`${ac.class}|${ac.section}`}
                          onChange={(e) => {
                            const v = e.target.value;
                            const [c, s] = v.includes('|') ? v.split('|') : [v, ''];
                            setGates((g) => {
                              const next = [...g];
                              const allowed = [...(next[idx].allowedClasses || [])];
                              if (allowed[cIdx]) {
                                allowed[cIdx] = { ...allowed[cIdx], class: c ?? '', section: s ?? '' };
                                next[idx] = { ...next[idx], allowedClasses: allowed };
                              }
                              return next;
                            });
                          }}
                          sx={{ minWidth: 160 }}
                          disabled={!canEditGates}
                        >
                          <MenuItem value="|">— Select —</MenuItem>
                          {classSectionsList
                            .filter(
                              (cs) =>
                                !(gate.allowedClasses || []).some(
                                  (other, i) =>
                                    i !== cIdx &&
                                    String(other.class) === String(cs.class) &&
                                    String(other.section || '') === String(cs.section || '')
                                )
                            )
                            .map((cs, i) => (
                              <MenuItem key={i} value={`${cs.class}|${cs.section}`}>
                                {cs.class}{cs.section ? ` - ${cs.section}` : ''}
                              </MenuItem>
                            ))}
                        </TextField>
                      ) : (
                        <>
                          <TextField size="small" placeholder="Class" value={ac.class} onChange={(e) => updateAllowedClass(idx, cIdx, 'class', e.target.value)} sx={{ width: 80 }} disabled={!canEditGates} />
                          <TextField size="small" placeholder="Section" value={ac.section} onChange={(e) => updateAllowedClass(idx, cIdx, 'section', e.target.value)} sx={{ width: 80 }} disabled={!canEditGates} />
                        </>
                      )}
                      <TextField
                        size="small"
                        label="From"
                        type="time"
                        value={ac.startTime ?? ''}
                        onChange={(e) => updateAllowedClass(idx, cIdx, 'startTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                        placeholder="14:00"
                        disabled={!canEditGates}
                      />
                      <TextField
                        size="small"
                        label="To"
                        type="time"
                        value={ac.endTime ?? ''}
                        onChange={(e) => updateAllowedClass(idx, cIdx, 'endTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                        placeholder="16:00"
                        disabled={!canEditGates}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={ac.requireQRScanAtGate !== false}
                            onChange={() => updateAllowedClass(idx, cIdx, 'requireQRScanAtGate', ac.requireQRScanAtGate === false)}
                            disabled={!canEditGates}
                          />
                        }
                        label="Allow QR scan"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!ac.allowRequestWithoutQR}
                            onChange={() => updateAllowedClass(idx, cIdx, 'allowRequestWithoutQR', !ac.allowRequestWithoutQR)}
                            disabled={!canEditGates}
                          />
                        }
                        label="Enable Request Button"
                      />
                      {canEditGates && <IconButton size="small" onClick={() => removeAllowedClass(idx, cIdx)}><DeleteIcon fontSize="small" /></IconButton>}
                    </Box>
                  </Box>
                ))}
                {classSectionsList.length === 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Add classes in Classes page to use dropdowns</Typography>
                )}
                {canEditGates && <Button size="small" startIcon={<AddIcon />} onClick={() => addAllowedClass(idx)} sx={{ mt: 1 }}>Add class/section</Button>}
                {useTvDisplay && school && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }} component="span">
                    <>TV display URL: <code>?code={school.uniqueCode}&amp;gate={gate.gateId}</code> (open TV app with these params)</>
                  </Typography>
                )}
                {!useTvDisplay && school && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => openStaticQRPrintPage(String(school.uniqueCode ?? ''), String(school.name ?? ''), String(gate.gateId ?? ''), String(gate.name ?? ''), showToast)}
                    >
                      Get static QR for this gate (print &amp; put at gate)
                    </Link>
                  </Typography>
                )}

                {!useTvDisplay && school && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                    Display is disabled
                  </Typography>
                )}
              </Paper>
            ))}
            {canEditGates && <Button startIcon={<AddIcon />} onClick={addGate} variant="outlined">Add gate</Button>}
          </Box>
          {canEditGates && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={updateMutation.isLoading} sx={{ mt: 2 }}>Save gates</Button>
          )}
        </Paper>
      )}

      {currentTab === 'geofence' && school && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>School geofence</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Geofence ensures dispersal requests are only allowed when the parent is within school premises. Draw the school boundary on the map and save. Gate pickup points and request areas are shown.
          </Typography>
          <GeofenceMapEditor
            key={school._id}
            initialGeofence={school.geofence ? { type: 'Polygon', coordinates: Array.isArray(school.geofence.coordinates) ? JSON.parse(JSON.stringify(school.geofence.coordinates)) : [] } : undefined}
            gatePins={gates.map((g) => {
              const ra = g.requestArea as { type?: string; coordinates?: number[][][]; center?: number[]; radius?: number } | null | undefined;
              let requestArea: GeofenceGeoJSON | undefined;
              if (ra && typeof ra === 'object') {
                if (ra.type === 'Circle' && Array.isArray(ra.center) && ra.center.length >= 2 && ra.radius != null) {
                  requestArea = { type: 'Polygon', coordinates: [circleToGeoJSONRing(ra.center[1], ra.center[0], ra.radius)] };
                } else if (Array.isArray(ra.coordinates) && ra.coordinates.length > 0) {
                  requestArea = { type: 'Polygon', coordinates: JSON.parse(JSON.stringify(ra.coordinates)) };
                }
              }
              return { gateId: g.gateId!, name: g.name, latitude: g.latitude ?? undefined, longitude: g.longitude ?? undefined, requestArea };
            })}
            onSave={(geofence) => saveGeofenceMutation.mutate(geofence)}
            onClear={() => saveGeofenceMutation.mutate(null)}
            saving={saveGeofenceMutation.isLoading}
            canSave={canEditSchool}
          />
        </Paper>
      )}

      <Dialog
        open={gateMapDialogGateIdx !== null}
        onClose={() => setGateMapDialogGateIdx(null)}
        fullScreen
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%' } }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          {gateMapDialogGateIdx !== null && gates[gateMapDialogGateIdx] ? `Request area: ${gates[gateMapDialogGateIdx].name || 'Gate'}` : 'Gate on map'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {gateMapDialogGateIdx !== null && school && gates[gateMapDialogGateIdx] && (
            <GateMapEditor
              gateName={gates[gateMapDialogGateIdx].name || 'Gate'}
              fullScreen
              initialRequestArea={gates[gateMapDialogGateIdx].requestArea ?? undefined}
              schoolCenter={
                school.geofence?.coordinates?.[0]?.[0]
                  ? { lat: (school.geofence.coordinates[0][0] as number[])[1], lng: (school.geofence.coordinates[0][0] as number[])[0] }
                  : undefined
              }
              schoolGeofence={
                school.geofence && Array.isArray(school.geofence.coordinates) && school.geofence.coordinates.length > 0
                  ? { type: 'Polygon', coordinates: JSON.parse(JSON.stringify(school.geofence.coordinates)) }
                  : undefined
              }
              onSave={(result) => updateGateMapResult(gateMapDialogGateIdx, result)}
              onCancel={() => setGateMapDialogGateIdx(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Settings;
